import { useState } from 'react'

import { FilterPopoverPanel } from '@/components/filters/filter-dropdown-shell'
import { FILTER_POPOVER_CONTENT_CLASS } from '@/components/filters/filter-styles'
import { SegmentedFilterChip } from '@/components/filters/segmented-filter-chip'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export type FilterChipProps = {
  label: string
  value: string
  variant: 'primary' | 'secondary'
  popoverContent: React.ReactNode
  onRemove?: () => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
}

/**
 * Applied filter in a pill bar — segmented chip whose label/value segments
 * open the filter's edit popover; secondary chips carry a remove segment.
 */
export function FilterChip({
  label,
  value,
  variant,
  popoverContent,
  onRemove,
  open,
  onOpenChange,
  className,
}: FilterChipProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isOpen = open ?? uncontrolledOpen
  const setOpen = (next: boolean) => {
    setUncontrolledOpen(next)
    onOpenChange?.(next)
  }
  const openEditor = () => setOpen(true)

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <SegmentedFilterChip
          className={className}
          label={label}
          value={value}
          onLabelClick={openEditor}
          onValueClick={openEditor}
          onRemove={variant === 'secondary' ? onRemove : undefined}
          labelAriaLabel={`${label}: ${value}. Click to edit filter.`}
          valueAriaLabel={`${label}: ${value}. Click to edit filter.`}
          removeAriaLabel={`Remove ${label} filter`}
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className={cn(
          'w-auto min-w-[16rem] max-w-[min(100vw-2rem,24rem)] p-0',
          FILTER_POPOVER_CONTENT_CLASS,
        )}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <FilterPopoverPanel>{popoverContent}</FilterPopoverPanel>
      </PopoverContent>
    </Popover>
  )
}
